/**
 * Whether we are online, and how many writes are waiting.
 *
 * `navigator.onLine` is famously optimistic — it means "there is a network
 * interface", not "the internet works", so a captive portal reads as online. That
 * is fine for what it drives here: a banner and a drain attempt, both of which are
 * harmless when wrong. Nothing is gated on it.
 */

import { useEffect, useState } from 'react'
import { subscribeToQueue } from './queue'

export function useConnectivity(): { online: boolean; queued: number } {
  const [online, setOnline] = useState(() => navigator.onLine)
  const [queued, setQueued] = useState(0)

  useEffect(() => {
    const goOnline = () => setOnline(true)
    const goOffline = () => setOnline(false)
    window.addEventListener('online', goOnline)
    window.addEventListener('offline', goOffline)
    return () => {
      window.removeEventListener('online', goOnline)
      window.removeEventListener('offline', goOffline)
    }
  }, [])

  useEffect(() => subscribeToQueue((queue) => setQueued(queue.length)), [])

  return { online, queued }
}
